import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PARTS, CATEGORIES } from '../../data/parts';
import * as api from '../../services/api';
import styles from './ConfigurationManagement.module.css';

export default function ConfigurationManagement() {
    const { t } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const fileInputRef = useRef(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [officialFilter, setOfficialFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Data
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });

    // Modal states
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState({});
    const [processing, setProcessing] = useState(false);
    const [uploadingImages, setUploadingImages] = useState(false);

    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState(null); // 'config' | 'media-{id}'

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Load configurations
    useEffect(() => {
        loadConfigs();
    }, [statusFilter, officialFilter, debouncedSearch, pagination.page]);

    // Handle URL param for selected config
    useEffect(() => {
        const configId = searchParams.get('id');
        if (configId && configs.length > 0) {
            const config = configs.find(c => c.id === configId);
            if (config) {
                loadConfigDetail(configId);
            }
        }
    }, [searchParams, configs]);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
            };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (officialFilter !== 'all') params.is_official = officialFilter === 'official';
            if (debouncedSearch) params.search = debouncedSearch;

            const result = await api.getAllAdminConfigurations(params);
            if (result.success) {
                setConfigs(result.data || []);
                setPagination(prev => ({ ...prev, total: result.total || 0 }));
            }
        } catch (err) {
            console.error('Failed to load configs:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadConfigDetail = async (id) => {
        try {
            const result = await api.getAdminConfigurationDetail(id);
            if (result.success) {
                setSelectedConfig(result.data);
                setEditData({
                    title: result.data.title,
                    description: result.data.description || '',
                    status: result.data.status,
                    is_official: result.data.is_official,
                });
            }
        } catch (err) {
            console.error('Failed to load config detail:', err);
        }
    };

    const openConfig = (config) => {
        loadConfigDetail(config.id);
        setSearchParams({ id: config.id });
        setEditMode(false);
    };

    const closeModal = () => {
        setSelectedConfig(null);
        setEditMode(false);
        setEditData({});
        setDeleteConfirm(null);
        setSearchParams({});
    };

    const handleSaveEdit = async () => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.updateAdminConfiguration(selectedConfig.id, editData);
            if (result.success) {
                setSelectedConfig({ ...selectedConfig, ...editData });
                setEditMode(false);
                loadConfigs(); // Refresh list
            }
        } catch (err) {
            console.error('Save failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleOfficial = async () => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.toggleOfficialStatus(selectedConfig.id);
            if (result.success) {
                const newValue = !selectedConfig.is_official;
                setSelectedConfig({ ...selectedConfig, is_official: newValue });
                setEditData(prev => ({ ...prev, is_official: newValue }));
                loadConfigs();
            }
        } catch (err) {
            console.error('Toggle official failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleSetCover = async (mediaUrl) => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.setConfigCoverImage(selectedConfig.id, mediaUrl);
            if (result.success) {
                setSelectedConfig({ ...selectedConfig, cover_image_url: mediaUrl });
                loadConfigs();
            }
        } catch (err) {
            console.error('Set cover failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteMedia = async (mediaId) => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.deleteConfigMedia(selectedConfig.id, mediaId);
            if (result.success) {
                setSelectedConfig({
                    ...selectedConfig,
                    media: selectedConfig.media.filter(m => m.id !== mediaId)
                });
                setDeleteConfirm(null);
            }
        } catch (err) {
            console.error('Delete media failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.deleteAdminConfiguration(selectedConfig.id);
            if (result.success) {
                closeModal();
                loadConfigs();
            }
        } catch (err) {
            console.error('Delete config failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleUploadImages = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0 || !selectedConfig) return;

        setUploadingImages(true);
        try {
            const result = await api.uploadAdminConfigurationImages(selectedConfig.id, files);
            if (result.success) {
                // Reload config detail to get new media
                loadConfigDetail(selectedConfig.id);
            }
        } catch (err) {
            console.error('Upload failed:', err);
        } finally {
            setUploadingImages(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const getPartName = (category, partId) => {
        const part = PARTS[category]?.find(p => p.id === partId);
        return part ? (t(part.nameKey) || part.id) : partId;
    };

    const totalPages = Math.ceil(pagination.total / pagination.limit);

    return (
        <div className={styles.management}>
            <h1 className={styles.title}>
                {t('admin.management.title') || 'Configuration Management'}
            </h1>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label>{t('admin.management.status') || 'Status'}</label>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <option value="all">{t('admin.management.all') || 'All'}</option>
                        <option value="draft">{t('admin.management.draft') || 'Draft'}</option>
                        <option value="pending">{t('admin.management.pending') || 'Pending'}</option>
                        <option value="approved">{t('admin.management.approved') || 'Approved'}</option>
                        <option value="rejected">{t('admin.management.rejected') || 'Rejected'}</option>
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label>{t('admin.management.type') || 'Type'}</label>
                    <select
                        value={officialFilter}
                        onChange={e => setOfficialFilter(e.target.value)}
                    >
                        <option value="all">{t('admin.management.all') || 'All'}</option>
                        <option value="official">{t('admin.management.official') || 'Official'}</option>
                        <option value="user">{t('admin.management.userContent') || 'User Content'}</option>
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label>{t('admin.management.search') || 'Search'}</label>
                    <input
                        type="text"
                        placeholder={t('admin.management.searchPlaceholder') || 'Search by title...'}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Results count */}
            <div className={styles.resultsCount}>
                {t('admin.management.showing') || 'Showing'} {configs.length} {t('admin.management.of') || 'of'} {pagination.total} {t('admin.management.configs') || 'configurations'}
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>
                    {t('common.loading') || 'Loading...'}
                </div>
            ) : configs.length === 0 ? (
                <div className={styles.empty}>
                    <p>{t('admin.management.noItems') || 'No configurations found'}</p>
                </div>
            ) : (
                <>
                    <div className={styles.grid}>
                        {configs.map(config => (
                            <div
                                key={config.id}
                                className={styles.card}
                                onClick={() => openConfig(config)}
                            >
                                <div className={styles.cardImage}>
                                    {config.cover_image_url ? (
                                        <img src={config.cover_image_url} alt={config.title} />
                                    ) : (
                                        <div className={styles.noImage}>{t('common.noImage')}</div>
                                    )}
                                    <div className={styles.badges}>
                                        <span className={`${styles.statusBadge} ${styles[config.status]}`}>
                                            {t(`admin.management.${config.status}`)}
                                        </span>
                                        {config.is_official && (
                                            <span className={styles.officialBadge}>{t('common.official')}</span>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.cardContent}>
                                    <h3>{config.title}</h3>
                                    <p className={styles.author}>
                                        {t('admin.management.by') || 'by'} {config.user?.nickname || 'Unknown'}
                                    </p>
                                    <div className={styles.stats}>
                                        <span>❤️ {config.likes_count || 0}</span>
                                        <span>⭐ {config.favorites_count || 0}</span>
                                        <span>💬 {config.comments_count || 0}</span>
                                    </div>
                                    <p className={styles.date}>
                                        {new Date(config.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            >
                                {t('common.prev') || 'Previous'}
                            </button>
                            <span>
                                {pagination.page} / {totalPages}
                            </span>
                            <button
                                disabled={pagination.page >= totalPages}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            >
                                {t('common.next') || 'Next'}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Detail/Edit Modal */}
            {selectedConfig && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <button className={styles.closeBtn} onClick={closeModal}>×</button>

                        <div className={styles.modalContent}>
                            {/* Media Section */}
                            <div className={styles.mediaSection}>
                                <div className={styles.mainImage}>
                                    {selectedConfig.cover_image_url ? (
                                        <img src={selectedConfig.cover_image_url} alt="" />
                                    ) : (
                                        <div className={styles.noImage}>{t('common.noImage')}</div>
                                    )}
                                </div>

                                {/* Media Gallery with management */}
                                <div className={styles.mediaGallery}>
                                    <div className={styles.galleryHeader}>
                                        <h4>{t('admin.management.media') || 'Media'}</h4>
                                        <button
                                            className={styles.uploadBtn}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingImages}
                                        >
                                            {uploadingImages ? '...' : (t('admin.management.addImages') || '+ Add Images')}
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={handleUploadImages}
                                        />
                                    </div>

                                    <div className={styles.mediaItems}>
                                        {selectedConfig.media?.map((m) => (
                                            <div key={m.id} className={`${styles.mediaItem} ${m.media_type === 'video' ? styles.videoItem : ''}`}>
                                                {m.media_type === 'video' ? (
                                                    <video src={m.url} controls preload="metadata" />
                                                ) : (
                                                    <img src={m.url || m.thumbnail_url} alt="" />
                                                )}
                                                <div className={styles.mediaActions}>
                                                    {m.media_type === 'video' && (
                                                        <span className={styles.videoIndicator}>Video</span>
                                                    )}
                                                    {m.media_type !== 'video' && selectedConfig.cover_image_url !== m.url && (
                                                        <button
                                                            className={styles.setCoverBtn}
                                                            onClick={() => handleSetCover(m.url)}
                                                            disabled={processing}
                                                            title={t('admin.management.setCover') || 'Set as cover'}
                                                        >
                                                            🖼️
                                                        </button>
                                                    )}
                                                    {m.media_type !== 'video' && selectedConfig.cover_image_url === m.url && (
                                                        <span className={styles.coverIndicator}>Cover</span>
                                                    )}
                                                    <button
                                                        className={styles.deleteMediaBtn}
                                                        onClick={() => setDeleteConfirm(`media-${m.id}`)}
                                                        disabled={processing}
                                                        title={t('admin.management.delete') || 'Delete'}
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>

                                                {/* Delete media confirmation */}
                                                {deleteConfirm === `media-${m.id}` && (
                                                    <div className={styles.deleteOverlay}>
                                                        <p>{m.media_type === 'video'
                                                            ? (t('admin.management.confirmDeleteVideo') || 'Delete this video?')
                                                            : (t('admin.management.confirmDeleteMedia') || 'Delete this image?')
                                                        }</p>
                                                        <div className={styles.deleteActions}>
                                                            <button onClick={() => handleDeleteMedia(m.id)} disabled={processing}>
                                                                {t('common.yes') || 'Yes'}
                                                            </button>
                                                            <button onClick={() => setDeleteConfirm(null)}>
                                                                {t('common.no') || 'No'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {(!selectedConfig.media || selectedConfig.media.length === 0) && (
                                            <p className={styles.noMedia}>{t('admin.management.noMedia') || 'No media uploaded'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Info Section */}
                            <div className={styles.infoSection}>
                                {!editMode ? (
                                    <>
                                        <div className={styles.infoHeader}>
                                            <h2>{selectedConfig.title}</h2>
                                            <div className={styles.infoActions}>
                                                <button
                                                    className={styles.editBtn}
                                                    onClick={() => setEditMode(true)}
                                                >
                                                    {t('admin.management.edit') || 'Edit'}
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.statusInfo}>
                                            <span className={`${styles.statusBadge} ${styles[selectedConfig.status]}`}>
                                                {t(`admin.management.${selectedConfig.status}`)}
                                            </span>
                                            <button
                                                className={`${styles.officialToggle} ${selectedConfig.is_official ? styles.active : ''}`}
                                                onClick={handleToggleOfficial}
                                                disabled={processing}
                                            >
                                                {selectedConfig.is_official
                                                    ? (t('admin.management.removeOfficial') || '★ Official')
                                                    : (t('admin.management.makeOfficial') || '☆ Make Official')
                                                }
                                            </button>
                                        </div>

                                        <p className={styles.submitter}>
                                            {t('admin.management.author') || 'Author'}: {selectedConfig.user?.nickname || 'Unknown'}
                                            <br />
                                            {t('admin.management.created') || 'Created'}: {new Date(selectedConfig.created_at).toLocaleString()}
                                            {selectedConfig.reviewed_by && (
                                                <>
                                                    <br />
                                                    {t('admin.management.reviewedBy') || 'Reviewed by'}: {selectedConfig.reviewer?.nickname}
                                                </>
                                            )}
                                        </p>

                                        {selectedConfig.description && (
                                            <div className={styles.description}>
                                                <h4>{t('admin.management.description') || 'Description'}</h4>
                                                <p>{selectedConfig.description}</p>
                                            </div>
                                        )}

                                        <div className={styles.configDetails}>
                                            <h4>{t('admin.management.configuration') || 'Configuration'}</h4>
                                            <ul>
                                                {CATEGORIES.map(cat => {
                                                    const value = selectedConfig.config_data?.[cat.id];
                                                    if (!value) return null;
                                                    if (Array.isArray(value) && value.length === 0) return null;

                                                    return (
                                                        <li key={cat.id}>
                                                            <span className={styles.catLabel}>{t(cat.nameKey) || cat.id}:</span>
                                                            <span className={styles.catValue}>
                                                                {Array.isArray(value)
                                                                    ? value.map(v => getPartName(cat.id, v)).join(', ')
                                                                    : getPartName(cat.id, value)
                                                                }
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                            <div className={styles.totalPrice}>
                                                {t('admin.management.totalPrice') || 'Total'}: ¥{selectedConfig.total_price?.toLocaleString()}
                                            </div>
                                        </div>

                                        {/* Engagement Stats */}
                                        <div className={styles.engagementStats}>
                                            <div className={styles.stat}>
                                                <span className={styles.statIcon}>❤️</span>
                                                <span className={styles.statValue}>{selectedConfig.likes_count || 0}</span>
                                                <span className={styles.statLabel}>{t('admin.management.likes') || 'Likes'}</span>
                                            </div>
                                            <div className={styles.stat}>
                                                <span className={styles.statIcon}>⭐</span>
                                                <span className={styles.statValue}>{selectedConfig.favorites_count || 0}</span>
                                                <span className={styles.statLabel}>{t('admin.management.favorites') || 'Favorites'}</span>
                                            </div>
                                            <div className={styles.stat}>
                                                <span className={styles.statIcon}>💬</span>
                                                <span className={styles.statValue}>{selectedConfig.comments_count || 0}</span>
                                                <span className={styles.statLabel}>{t('admin.management.comments') || 'Comments'}</span>
                                            </div>
                                            <div className={styles.stat}>
                                                <span className={styles.statIcon}>👁️</span>
                                                <span className={styles.statValue}>{selectedConfig.views_count || 0}</span>
                                                <span className={styles.statLabel}>{t('admin.management.views') || 'Views'}</span>
                                            </div>
                                        </div>

                                        {/* Delete Config Button */}
                                        <div className={styles.dangerZone}>
                                            <h4>{t('admin.management.dangerZone') || 'Danger Zone'}</h4>
                                            {deleteConfirm === 'config' ? (
                                                <div className={styles.deleteConfirm}>
                                                    <p>{t('admin.management.confirmDelete') || 'Are you sure you want to delete this configuration? This action cannot be undone.'}</p>
                                                    <div className={styles.deleteConfirmActions}>
                                                        <button
                                                            className={styles.deleteConfirmBtn}
                                                            onClick={handleDeleteConfig}
                                                            disabled={processing}
                                                        >
                                                            {processing ? '...' : (t('admin.management.yesDelete') || 'Yes, Delete')}
                                                        </button>
                                                        <button
                                                            className={styles.cancelBtn}
                                                            onClick={() => setDeleteConfirm(null)}
                                                        >
                                                            {t('common.cancel') || 'Cancel'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    className={styles.deleteConfigBtn}
                                                    onClick={() => setDeleteConfirm('config')}
                                                >
                                                    {t('admin.management.deleteConfig') || 'Delete Configuration'}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    /* Edit Mode */
                                    <div className={styles.editForm}>
                                        <h3>{t('admin.management.editConfig') || 'Edit Configuration'}</h3>

                                        <div className={styles.formGroup}>
                                            <label>{t('admin.management.title') || 'Title'}</label>
                                            <input
                                                type="text"
                                                value={editData.title || ''}
                                                onChange={e => setEditData(prev => ({ ...prev, title: e.target.value }))}
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>{t('admin.management.description') || 'Description'}</label>
                                            <textarea
                                                rows={4}
                                                value={editData.description || ''}
                                                onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>{t('admin.management.status') || 'Status'}</label>
                                            <select
                                                value={editData.status || 'draft'}
                                                onChange={e => setEditData(prev => ({ ...prev, status: e.target.value }))}
                                            >
                                                <option value="draft">{t('admin.management.draft')}</option>
                                                <option value="pending">{t('admin.management.pending')}</option>
                                                <option value="approved">{t('admin.management.approved')}</option>
                                                <option value="rejected">{t('admin.management.rejected')}</option>
                                            </select>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={editData.is_official || false}
                                                    onChange={e => setEditData(prev => ({ ...prev, is_official: e.target.checked }))}
                                                />
                                                {t('admin.management.isOfficial') || 'Official Content'}
                                            </label>
                                        </div>

                                        <div className={styles.editActions}>
                                            <button
                                                className={styles.saveBtn}
                                                onClick={handleSaveEdit}
                                                disabled={processing}
                                            >
                                                {processing ? '...' : (t('common.save') || 'Save')}
                                            </button>
                                            <button
                                                className={styles.cancelBtn}
                                                onClick={() => setEditMode(false)}
                                            >
                                                {t('common.cancel') || 'Cancel'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
